import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

export default function ArchivePage() {
  const api = useApi();
  const { currentUser } = useAuth();
  const [myHistory, setMyHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearData, setYearData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.getArchiveYears()
      .then(data => {
        setYears(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) setSelectedYear(data[0].year);
      })
      .catch(err => {
        console.error('Archive load error:', err);
        setYears([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    setHistoryLoading(true);
    api.getMyBracketHistory()
      .then(data => setMyHistory(data.history || []))
      .catch(err => console.warn('History load:', err.message))
      .finally(() => setHistoryLoading(false));
  }, [currentUser]);

  useEffect(() => {
    if (!selectedYear) return;
    api.getArchiveYear(selectedYear)
      .then(setYearData)
      .catch(console.error);
  }, [selectedYear]);

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading archive...</div>
    </div>
  );

  const filteredEntries = (yearData?.entries || []).filter(e =>
    !search || e.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">POOL <span>ARCHIVE</span></h1>
        <p className="page-subtitle">Historical leaderboards from past tournaments</p>
      </div>

      {/* ── PERSONAL ENTRY ARCHIVE ────────────────────────────── */}
      {currentUser && (myHistory.length > 0 || historyLoading) && (
        <div className="card" style={{ marginBottom: 32 }}>
          <div className="card-header">
            <h2 className="card-title" style={{ fontSize: '1.4rem' }}>MY ENTRIES</h2>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>
              Your bracket history from 2025 onward
            </span>
          </div>

          {historyLoading ? (
            <div style={{ padding: 20, color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>Loading...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {myHistory.map((yearGroup, yi) => (
                <div key={yearGroup.year}>
                  {/* Year header row */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--slate)',
                    marginTop: yi > 0 ? 8 : 0,
                  }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--blue)', minWidth: 60 }}>
                      {yearGroup.year}
                    </span>
                    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)' }}>
                      {yearGroup.entries.length} bracket{yearGroup.entries.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Entries for that year */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {yearGroup.entries.map((entry, ei) => (
                      <div key={entry.id} style={{
                        display: 'flex', alignItems: 'center', gap: 16,
                        padding: '10px 4px',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                      }}>
                        <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '0.95rem', color: 'var(--cream)', flex: 1 }}>
                          {entry.name}
                        </span>
                        {entry.sleeperTeamId && (
                          <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.78rem', color: 'var(--gold)', background: 'rgba(241,196,15,0.1)', border: '1px solid var(--gold)', borderRadius: 4, padding: '1px 7px' }}>
                            ★ {entry.sleeperTeamId}
                          </span>
                        )}
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--blue)', minWidth: 40, textAlign: 'right' }}>
                          {entry.score}
                        </span>
                        <a href={`/bracket/${entry.id}`} style={{
                          fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--blue)',
                          textDecoration: 'none', border: '1px solid var(--blue)',
                          borderRadius: 5, padding: '3px 10px',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.target.style.background = 'var(--blue)'; e.target.style.color = 'white'; }}
                        onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = 'var(--blue)'; }}>
                          View →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── POOL ARCHIVE ──────────────────────────────────────── */}
      {years.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--gray)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📚</div>
          <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>No archive data yet.</p>
          <p style={{ fontSize: '0.9rem', marginTop: 8 }}>An admin can add historical results from the Admin panel.</p>
        </div>
      ) : (
        <>

          {/* Year selector */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
            {years.map(y => (
              <button
                key={y.year}
                onClick={() => setSelectedYear(y.year)}
                style={{
                  padding: '10px 20px',
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.3rem',
                  letterSpacing: '1px',
                  background: selectedYear === y.year ? 'var(--blue)' : 'var(--navy-mid)',
                  border: `2px solid ${selectedYear === y.year ? 'var(--blue)' : 'var(--slate)'}`,
                  borderRadius: 8,
                  color: selectedYear === y.year ? 'var(--white)' : 'var(--gray)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {y.year}
                <span style={{ display: 'block', fontSize: '0.65rem', fontFamily: 'var(--font-condensed)', color: selectedYear === y.year ? 'rgba(255,255,255,0.7)' : 'var(--gray)', letterSpacing: '0.5px' }}>
                  {y.entryCount} entries
                </span>
              </button>
            ))}
          </div>

          {yearData && (
            <div>
              {/* Year header */}
              <div className="card" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {yearData.logoUrl && (
                      <img src={yearData.logoUrl} alt="logo" style={{
                        height: 64, width: 64, objectFit: 'contain',
                        borderRadius: 8, background: 'white', padding: 4,
                        border: '1px solid var(--slate)',
                      }} />
                    )}
                    <div>
                      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--cream)', letterSpacing: '3px', lineHeight: 1 }}>
                        {yearData.year} <span style={{ color: 'var(--blue)' }}>TOURNAMENT</span>
                      </h2>
                      {yearData.notes && (
                        <p style={{ color: 'var(--gray)', marginTop: 8, fontFamily: 'var(--font-condensed)' }}>{yearData.notes}</p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {yearData.champion && (
                      <div className="stat-box" style={{ textAlign: 'center', minWidth: 120 }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🏆</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>Champion</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, color: 'var(--blue-light)' }}>{yearData.champion}</div>
                      </div>
                    )}
                    {yearData.runnerUp && (
                      <div className="stat-box" style={{ textAlign: 'center', minWidth: 120 }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🏅</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>Runner-Up</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, color: 'var(--cream)' }}>{yearData.runnerUp}</div>
                      </div>
                    )}
                    {yearData.finalScore && (
                      <div className="stat-box" style={{ textAlign: 'center', minWidth: 120 }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🎯</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.75rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>Final Score</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, color: 'var(--cream)' }}>{yearData.finalScore}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top 3 podium — uses position (not rank) since tiebreakers already applied */}
              {filteredEntries.length >= 3 && !search && (
                <div className="grid-3" style={{ marginBottom: 24 }}>
                  {[1, 0, 2].map(posIdx => {
                    const entry = filteredEntries[posIdx];
                    if (!entry) return null;
                    const displayRank = posIdx + 1;
                    return (
                      <div key={posIdx} className="stat-box" style={{
                        border: displayRank === 1 ? '2px solid var(--gold)' : '1px solid var(--slate)',
                        transform: displayRank === 1 ? 'scale(1.04)' : 'scale(1)',
                      }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>{medals[displayRank]}</div>
                        <div className="stat-value">{entry.score}</div>
                        <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--cream)', marginTop: 6 }}>{entry.name}</div>
                        {entry.bracket && <div style={{ fontSize: '0.8rem', color: 'var(--gray)', marginTop: 2 }}>{entry.bracket}</div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Full table */}
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">{yearData.year} RESULTS</h2>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.9rem', color: 'var(--gray)' }}>
                      {yearData.entryCount} entries
                    </span>
                    <input className="form-input" type="text" placeholder="Search..." value={search}
                      onChange={e => setSearch(e.target.value)} style={{ width: 160 }} />
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Rank</th>
                        <th>Name</th>
                        {yearData.entries.some(e => e.bracket) && <th>Bracket</th>}
                        <th style={{ textAlign: 'right' }}>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEntries.map((entry, i) => {
                        // Use position (i+1) for medals/rank display — tiebreakers already applied
                        const pos = i + 1;
                        return (
                        <tr key={i} className={`rank-${pos <= 3 ? pos : entry.rank}`}>
                          <td><span className="rank-number">{medals[pos] || `#${pos}`}</span></td>
                          <td style={{ fontWeight: pos <= 3 ? 700 : 400 }}>{entry.name}</td>
                          {yearData.entries.some(e => e.bracket) && (
                            <td style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{entry.bracket || ''}</td>
                          )}
                          <td style={{ textAlign: 'right' }}><span className="score-value">{entry.score}</span></td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
